import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ROLE_BADGE_CLASSES } from "@/components/Topbar";
import { SUPPORTED_LANGUAGES } from "@/i18n";

const ROLES = ["admin", "accountant", "user"];

function UserDialog({ mode, open, onOpenChange, initial, onSubmit, submitting }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
    preferred_language: "en",
    is_active: true,
  });

  useEffect(() => {
    if (open) {
      if (mode === "edit" && initial) {
        setForm({
          name: initial.name || "",
          email: initial.email || "",
          password: "",
          role: initial.role || "user",
          preferred_language: initial.preferred_language || "en",
          is_active: !!initial.is_active,
        });
      } else {
        setForm({
          name: "",
          email: "",
          password: "",
          role: "user",
          preferred_language: "en",
          is_active: true,
        });
      }
    }
  }, [open, mode, initial]);

  const titleKey = mode === "edit" ? "users.edit.title" : "users.create.title";
  const descKey = mode === "edit" ? "users.edit.description" : "users.create.description";

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    await onSubmit(form);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid={`user-dialog-${mode}`}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight">
            {t(titleKey)}
          </DialogTitle>
          <DialogDescription>{t(descKey)}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="user-name">{t("users.form.name")}</Label>
            <Input
              id="user-name"
              data-testid="user-form-name"
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="user-email">{t("users.form.email")}</Label>
            <Input
              id="user-email"
              type="email"
              required
              disabled={mode === "edit"}
              data-testid="user-form-email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
            />
          </div>

          {mode === "create" && (
            <div className="space-y-2">
              <Label htmlFor="user-password">{t("users.form.password")}</Label>
              <Input
                id="user-password"
                type="password"
                required
                minLength={6}
                data-testid="user-form-password"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{t("users.form.role")}</Label>
              <Select
                value={form.role}
                onValueChange={(v) => set("role", v)}
              >
                <SelectTrigger data-testid="user-form-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`user-form-role-${r}`}>
                      {t(`roles.${r}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("users.form.language")}</Label>
              <Select
                value={form.preferred_language}
                onValueChange={(v) => set("preferred_language", v)}
              >
                <SelectTrigger data-testid="user-form-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((lng) => (
                    <SelectItem key={lng} value={lng}>
                      {t(`languages.${lng}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {mode === "edit" && (
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <Label htmlFor="user-active" className="cursor-pointer">
                {t("users.form.active")}
              </Label>
              <Switch
                id="user-active"
                checked={form.is_active}
                onCheckedChange={(v) => set("is_active", v)}
                data-testid="user-form-active"
              />
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="user-form-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="user-form-submit"
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {mode === "edit" ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { t } = useTranslation();
  const { user: currentUser, company } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [dialogState, setDialogState] = useState({ open: false, mode: "create", initial: null });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err, t("users.toasts.error")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => (a.created_at < b.created_at ? -1 : 1)),
    [users]
  );

  function openCreate() {
    setDialogState({ open: true, mode: "create", initial: null });
  }

  function openEdit(u) {
    setDialogState({ open: true, mode: "edit", initial: u });
  }

  async function handleSubmit(form) {
    setSubmitting(true);
    try {
      if (dialogState.mode === "create") {
        await api.post("/users", form);
        toast.success(t("users.toasts.created"));
      } else {
        const payload = {
          name: form.name,
          role: form.role,
          preferred_language: form.preferred_language,
          is_active: form.is_active,
        };
        await api.patch(`/users/${dialogState.initial.id}`, payload);
        toast.success(t("users.toasts.updated"));
      }
      setDialogState({ open: false, mode: "create", initial: null });
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("users.toasts.error")));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setSubmitting(true);
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success(t("users.toasts.deleted"));
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("users.toasts.error")));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-8" data-testid="users-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <h1 className="font-heading text-4xl tracking-tight text-slate-900">
            {t("users.title")}
          </h1>
          <p className="max-w-xl text-sm leading-relaxed text-slate-500">
            {t("users.subtitle", { company: company?.name || "—" })}
          </p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="users-add-button"
          className="gap-2 bg-slate-900 text-white hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          {t("users.addUser")}
        </Button>
      </header>

      <div className="mobile-cards overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("users.table.name")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("users.table.email")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("users.table.role")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("users.table.language")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("users.table.status")}
              </TableHead>
              <TableHead className="text-end text-xs uppercase tracking-wide text-slate-500">
                {t("common.actions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            )}
            {!loading && sortedUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">
                  {t("users.empty")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              sortedUsers.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium text-slate-900" data-label={t("users.table.name")}>{u.name}</TableCell>
                  <TableCell className="text-slate-600" data-label={t("users.table.email")}>{u.email}</TableCell>
                  <TableCell data-label={t("users.table.role")}>
                    <Badge
                      className={`${ROLE_BADGE_CLASSES[u.role] || ROLE_BADGE_CLASSES.user}`}
                      data-testid={`user-role-${u.email}`}
                    >
                      {t(`roles.${u.role}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-600 uppercase text-xs tracking-wide" data-label={t("users.table.language")}>
                    {u.preferred_language}
                  </TableCell>
                  <TableCell data-label={t("users.table.status")}>
                    {u.is_active ? (
                      <span className="inline-flex items-center gap-2 text-xs text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        {t("users.table.active")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-xs text-slate-500">
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                        {t("users.table.inactive")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-end" data-label={t("common.actions")}>
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(u)}
                        data-testid={`user-edit-${u.email}`}
                        className="gap-1 text-slate-700"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        {t("common.edit")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmDelete(u)}
                        disabled={u.id === currentUser?.id}
                        data-testid={`user-delete-${u.email}`}
                        className="gap-1 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {t("common.delete")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <UserDialog
        mode={dialogState.mode}
        open={dialogState.open}
        onOpenChange={(o) =>
          setDialogState((s) => ({ ...s, open: o, initial: o ? s.initial : null }))
        }
        initial={dialogState.initial}
        onSubmit={handleSubmit}
        submitting={submitting}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="user-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("users.delete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("users.delete.description", {
                name: confirmDelete?.name || "",
                company: company?.name || "—",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={submitting}
              data-testid="user-delete-confirm-button"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {t("users.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
